import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'
import TextFieldV2 from 'core-system/TextFieldV2'
import FlexContainer from 'core-system/FlexContainer'
import palette from 'core-system/Themes/palette'
import OnboardingTasksDrawer from '../components/OnboardingTasksDrawer'
import { onboardingActions } from '../onboardingActions'
import onboardingService, {
  CommuterBenefitSetupFormData,
} from '../onboardingService'
import { useDispatch } from 'react-redux'

const Intro = styled(Text)`
  margin-bottom: 1.5rem;
  line-height: 1.5;
`

const BenefitCard = styled.div`
  border: 1px solid ${palette.grey.grey200};
  border-radius: 0.4375rem;
  padding: 1.25rem;
  margin-bottom: 1rem;
  background-color: ${palette.white};
`

const BenefitTitle = styled(Text)`
  font-weight: 600;
  margin-bottom: 0.75rem;
`

const QuestionLabel = styled(Text)`
  margin-bottom: 0.5rem;
  color: ${palette.text.secondary};
`

const YesNoGroup = styled.div`
  display: inline-flex;
  border: 0.0625rem solid ${palette.grey.grey300};
  border-radius: 0.5rem;
  overflow: hidden;
  background-color: ${palette.grey.grey100};
`

const YesNoOption = styled.button<{ $active: boolean }>`
  border: none;
  cursor: pointer;
  min-width: 4.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25;
  color: ${(props) =>
    props.$active ? palette.white : palette.primary.pink800};
  background-color: ${(props) =>
    props.$active ? palette.primary.pink800 : 'transparent'};
`

const NameField = styled.div`
  margin-top: 1rem;
`

const ErrorText = styled(Text)`
  margin-top: 0.75rem;
`

type ExistsAnswer = boolean | null

interface BenefitAnswers {
  exists: ExistsAnswer
  name: string
}

const EMPTY_ANSWERS: BenefitAnswers = {
  exists: null,
  name: '',
}

interface CommuterBenefitSetupDrawerProps {
  isOpen: boolean
  onClose: () => void
}

const CommuterBenefitSetupDrawer = ({
  isOpen,
  onClose,
}: CommuterBenefitSetupDrawerProps) => {
  const dispatch = useDispatch()
  const [transit, setTransit] = useState<BenefitAnswers>(EMPTY_ANSWERS)
  const [parking, setParking] = useState<BenefitAnswers>(EMPTY_ANSWERS)
  const [errorMessage, setErrorMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isFetching, setIsFetching] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      return
    }

    let cancelled = false
    const loadExisting = async () => {
      setIsFetching(true)
      setErrorMessage('')
      try {
        const response = await onboardingService.getCommuterBenefitSetupService()
        if (cancelled) {
          return
        }
        const data = response.data as CommuterBenefitSetupFormData
        setTransit({
          exists:
            typeof data.transitExists === 'boolean' ? data.transitExists : null,
          name: data.transitName || '',
        })
        setParking({
          exists:
            typeof data.parkingExists === 'boolean' ? data.parkingExists : null,
          name: data.parkingName || '',
        })
      } catch {
        if (!cancelled) {
          setTransit(EMPTY_ANSWERS)
          setParking(EMPTY_ANSWERS)
        }
      } finally {
        if (!cancelled) {
          setIsFetching(false)
        }
      }
    }

    loadExisting()
    return () => {
      cancelled = true
    }
  }, [isOpen])

  const handleClose = () => {
    setErrorMessage('')
    onClose()
  }

  const buildPayload = (draft: boolean): CommuterBenefitSetupFormData => ({
    transitExists: transit.exists,
    transitName: transit.exists === true ? transit.name.trim() : '',
    parkingExists: parking.exists,
    parkingName: parking.exists === true ? parking.name.trim() : '',
    draft,
  })

  const validate = (): boolean => {
    if (transit.exists === null) {
      setErrorMessage('Please answer whether a transit benefit already exists.')
      return false
    }
    if (parking.exists === null) {
      setErrorMessage('Please answer whether a parking benefit already exists.')
      return false
    }
    if (transit.exists && !transit.name.trim()) {
      setErrorMessage('Enter the transit benefit name or code.')
      return false
    }
    if (parking.exists && !parking.name.trim()) {
      setErrorMessage('Enter the parking benefit name or code.')
      return false
    }
    setErrorMessage('')
    return true
  }

  const handleSaveDraft = async () => {
    setIsLoading(true)
    setErrorMessage('')
    try {
      await onboardingService.saveCommuterBenefitSetupService(buildPayload(true))
      dispatch(
        onboardingActions.updateOnboardingSectionStatus({
          sectionType: 'COMMUTER_BENEFIT_SETUP',
          status: 'IN_PROGRESS',
        })
      )
      handleClose()
    } catch {
      setErrorMessage('Unable to save benefit setup. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async () => {
    if (!validate()) {
      return
    }

    setIsLoading(true)
    try {
      await onboardingService.saveCommuterBenefitSetupService(buildPayload(false))
      dispatch(
        onboardingActions.updateOnboardingSectionStatus({
          sectionType: 'COMMUTER_BENEFIT_SETUP',
          status: 'COMPLETED',
        })
      )
      handleClose()
    } catch {
      setErrorMessage('Unable to save benefit setup. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const renderBenefitModule = (
    title: string,
    answers: BenefitAnswers,
    onChange: (next: BenefitAnswers) => void
  ) => (
    <BenefitCard>
      <BenefitTitle variant='action2'>{title}</BenefitTitle>
      <QuestionLabel variant='body2'>
        Does this benefit already exist in your payroll system?
      </QuestionLabel>
      <YesNoGroup>
        <YesNoOption
          type='button'
          $active={answers.exists === true}
          onClick={() => onChange({ exists: true, name: answers.name })}
        >
          Yes
        </YesNoOption>
        <YesNoOption
          type='button'
          $active={answers.exists === false}
          onClick={() => onChange({ exists: false, name: '' })}
        >
          No
        </YesNoOption>
      </YesNoGroup>
      {answers.exists === true && (
        <NameField>
          <TextFieldV2
            label="What's it called or what's the code?"
            value={answers.name}
            onChange={(e) =>
              onChange({
                ...answers,
                name: e.target.value,
              })
            }
            placeholder='e.g. Commuter Transit or benefit code'
          />
        </NameField>
      )}
      {answers.exists === false && (
        <Text variant='body2' color={palette.text.secondary} marginTop='0.75rem'>
          We&apos;ll create this benefit in your payroll system.
        </Text>
      )}
    </BenefitCard>
  )

  return (
    <OnboardingTasksDrawer
      isOpen={isOpen}
      onClose={handleClose}
      primaryButton={{
        text: 'Save Benefit Setup',
        onClick: handleSubmit,
        loading: isLoading,
        disabled: isFetching || isLoading,
      }}
      secondaryButton={{
        text: 'Save and Complete Later',
        onClick: handleSaveDraft,
        loading: isLoading,
        disabled: isFetching || isLoading,
      }}
    >
      <FlexContainer flexDirection='column'>
        <Text variant='h3' marginBottom='0.5rem'>
          Commuter benefit setup
        </Text>
        <Intro variant='body1'>
          Tell us whether transit and parking benefits already exist in your
          payroll system. If they do, we&apos;ll register against them. If not,
          we&apos;ll create them.
        </Intro>
        {renderBenefitModule('Transit', transit, setTransit)}
        {renderBenefitModule('Parking', parking, setParking)}
        {errorMessage && (
          <ErrorText variant='body2' color={palette.error.red500}>
            {errorMessage}
          </ErrorText>
        )}
      </FlexContainer>
    </OnboardingTasksDrawer>
  )
}

if (process.env.NODE_ENV !== 'production') {
  CommuterBenefitSetupDrawer.displayName = 'CommuterBenefitSetupDrawer'
}

export default React.memo(CommuterBenefitSetupDrawer)
